import classNames from "classnames";
import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { NavLink as RouterNavLink } from "react-router-dom";

import { useSidebar } from "./SidebarContext";
import { SidebarMinimizedFlyoutPortal } from "./SidebarMinimizedFlyoutPortal";
import { logHoverMinimizedLabelProbe } from "./sidebarMinimizedLabelDebug";

interface NavLinkProps {
  item: {
    name: string;
    url?: string;
    modernizedUrl?: string;
    icon?: string;
    badge?: {
      variant: string;
      text: string;
    };
    class?: string;
    variant?: string;
  };
  itemKey: string | number;
  isActive: boolean;
  shouldShow: boolean;
  isDropdownItem?: boolean; // Indicates this link is inside a dropdown
}

const NavLink: React.FC<NavLinkProps> = ({
  item,
  itemKey,
  isActive,
  shouldShow,
  isDropdownItem = false,
}) => {
  const { isMinimized, setHoveredItemKey, compactStyle } = useSidebar();
  const [rippleStyle, setRippleStyle] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [isItemHovered, setIsItemHovered] = useState(false);
  const [minimizedFlyoutOpen, setMinimizedFlyoutOpen] = useState(false);
  const linkRef = useRef<HTMLAnchorElement>(null);
  const minimizedFlyoutHideTimerRef = useRef<number | null>(null);

  const labelEligible = shouldShow && isMinimized && !isDropdownItem;

  const hoverKey =
    typeof itemKey === "string" ? itemKey : `link-${itemKey}`;

  const clearMinimizedFlyoutHideTimer = useCallback(() => {
    if (minimizedFlyoutHideTimerRef.current !== null) {
      window.clearTimeout(minimizedFlyoutHideTimerRef.current);
      minimizedFlyoutHideTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => clearMinimizedFlyoutHideTimer();
  }, [clearMinimizedFlyoutHideTimer]);

  useEffect(() => {
    if (!isMinimized) {
      clearMinimizedFlyoutHideTimer();
      setMinimizedFlyoutOpen(false);
    }
  }, [isMinimized, clearMinimizedFlyoutHideTimer]);

  const handleLinkMouseEnter = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      clearMinimizedFlyoutHideTimer();
      if (labelEligible) {
        setMinimizedFlyoutOpen(true);
      }
      if (
        process.env.NODE_ENV === "development" &&
        typeof document !== "undefined" &&
        document.body.classList.contains("sidebar-minimized")
      ) {
        console.info("[SidebarNav] NavLink mouseenter", {
          item: item.name,
          labelEligible,
          isMinimizedFromContext: isMinimized,
          isDropdownItem,
        });
      }
      setHoveredItemKey(hoverKey);
      if (isDropdownItem) {
        setIsItemHovered(true);
      }
      if (labelEligible) {
        logHoverMinimizedLabelProbe(event.currentTarget, item.name);
      }
    },
    [
      hoverKey,
      isDropdownItem,
      isMinimized,
      item.name,
      labelEligible,
      clearMinimizedFlyoutHideTimer,
      setHoveredItemKey,
    ],
  );

  const handleLinkMouseLeave = useCallback(() => {
    clearMinimizedFlyoutHideTimer();
    if (labelEligible) {
      minimizedFlyoutHideTimerRef.current = window.setTimeout(() => {
        setMinimizedFlyoutOpen(false);
        minimizedFlyoutHideTimerRef.current = null;
      }, 120);
    }
    setHoveredItemKey(null);
    if (isDropdownItem) {
      setIsItemHovered(false);
    }
  }, [
    clearMinimizedFlyoutHideTimer,
    isDropdownItem,
    labelEligible,
    setHoveredItemKey,
  ]);

  if (!shouldShow) {
    return null;
  }

  // Modernized sidebar is now used everywhere - prefer modernizedUrl when available
  const url = item.modernizedUrl ?? item.url ?? "";
  const isExternalLink = url.substring(0, 4) === "http";

  const classes = {
    item: classNames("nav-item", item.class),
    link: classNames(
      "nav-link",
      item.variant ? `nav-link-${item.variant}` : "",
      { active: isActive },
    ),
    icon: classNames(item.icon),
  };

  const badge = (badgeData?: { variant: string; text: string }) => {
    if (!badgeData) {
      return null;
    }
    return (
      <span className={`badge badge-${badgeData.variant} ml-auto`}>
        {badgeData.text}
      </span>
    );
  };

  const iconStyle: React.CSSProperties =
    compactStyle === "option3" && !isDropdownItem
      ? {
          width: "16px",
          height: "16px",
          fontSize: "0.875rem",
          marginRight: "0.625rem",
          background: "transparent",
          border: "none",
          boxShadow: "none",
          borderRadius: 0,
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "var(--color-white)",
          WebkitTextFillColor: "var(--color-white)",
        }
      : isDropdownItem
        ? {
            marginRight: isMinimized ? "0.625rem" : "0.75rem",
            fontSize: isMinimized ? "0.875rem" : "1rem",
            width: isMinimized ? "16px" : "20px",
            height: isMinimized ? "16px" : "20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            transition: isMinimized
              ? "color 0.2s ease"
              : "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
            transform: isMinimized
              ? "translateZ(0)"
              : isItemHovered
                ? "scale(1.1) rotate(5deg) translateZ(0)"
                : "scale(1) rotate(0deg) translateZ(0)",
            filter: "none",
            backfaceVisibility: "hidden",
            WebkitFontSmoothing: "antialiased",
            textRendering: "optimizeLegibility",
            color:
              isItemHovered && isMinimized ? "var(--ajw-blue)" : "var(--color-white)",
            WebkitTextFillColor:
              isItemHovered && isMinimized ? "var(--ajw-blue)" : "var(--color-white)",
            imageRendering: "crisp-edges",
          }
        : {};

  const linkContent = (
    <>
      {item.icon && (
        <i
          className={classes.icon}
          aria-hidden={labelEligible ? true : undefined}
          data-minimized={isMinimized ? "true" : "false"}
          data-compact-style={compactStyle || undefined}
          style={iconStyle}
        />
      )}
      {(!isMinimized || isDropdownItem) && (
        <span
          className="nav-link-text"
          style={
            compactStyle === "option3" && !isDropdownItem
              ? {
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }
              : undefined
          }
        >
          {item.name}
        </span>
      )}
      {badge(item.badge)}
      {labelEligible ? (
        <span
          className="sidebar-minimized-flyout-label"
          aria-hidden="true"
        >
          {item.name}
        </span>
      ) : null}
    </>
  );

  const linkStyle: React.CSSProperties = {
    color: "var(--color-white)",
    textDecoration: "none",
    position: "relative",
    overflow: labelEligible ? "visible" : "hidden",
    ...(compactStyle === "option3" &&
      !isDropdownItem && {
        display: "flex",
        alignItems: "center",
        margin: "0.0625rem 0.375rem",
        padding: "0.375rem 0.625rem",
        minHeight: "32px",
        fontSize: "0.75rem",
        fontWeight: 500,
        letterSpacing: "0.02em",
        lineHeight: 1.5,
        borderRadius: "4px",
        border: "none",
        background: "transparent",
        boxShadow: "none",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
        cursor: "pointer",
      }),
    ...(isDropdownItem && {
      display: "flex",
      alignItems: "center",
      width: "100%",
      padding: isMinimized ? "0.5rem 0.875rem" : "0.5rem 1rem",
      margin: 0,
      fontWeight: 500,
      lineHeight: 1.4,
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
      minHeight: isMinimized ? "32px" : "32px",
      cursor: "pointer",
      transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
      borderRadius: isMinimized ? "6px" : "8px",
      background: isItemHovered
        ? "var(--color-white-10)"
        : "transparent",
      transform: isItemHovered
        ? isMinimized
          ? "translateX(4px) scale(1.02)"
          : "translateX(6px) scale(1.02)"
        : "translateX(0) scale(1)",
      boxShadow: isItemHovered
        ? isMinimized
          ? "0 2px 6px var(--ajw-blue-25)"
          : "0 2px 8px var(--ajw-blue-25)"
        : "none",
    }),
  };

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    setRippleStyle({ x, y });
    setTimeout(() => setRippleStyle(null), 600);
  };

  const sidebarLabel = labelEligible ? item.name : undefined;

  const linkElement = isExternalLink ? (
    <a
      ref={linkRef}
      href={url}
      className={classes.link}
      target="_blank"
      rel="noopener noreferrer"
      style={linkStyle}
      aria-label={sidebarLabel}
      data-sidebar-label={sidebarLabel}
      onMouseEnter={handleLinkMouseEnter}
      onMouseLeave={handleLinkMouseLeave}
      onClick={handleClick}
      tabIndex={0}
    >
      {linkContent}
      {rippleStyle && (
        <span
          className="ripple-effect"
          style={{
            position: "absolute",
            left: `${rippleStyle.x}px`,
            top: `${rippleStyle.y}px`,
            width: "20px",
            height: "20px",
            borderRadius: "50%",
            background: isDropdownItem
              ? "var(--ajw-blue-50)"
              : "var(--color-white-50)",
            transform: "translate(-50%, -50%)",
            pointerEvents: "none",
            animation: "ripple 0.6s ease-out",
            zIndex: 1,
          }}
        />
      )}
    </a>
  ) : (
    <RouterNavLink
      innerRef={linkRef}
      to={url}
      exact={false}
      isActive={(match) => Boolean(match) || isActive}
      className={classes.link}
      activeClassName="active"
      style={{
        ...linkStyle,
        ...(isActive && {
          background:
            "linear-gradient(90deg, var(--ajw-blue-40), var(--ajw-blue-25))",
          borderLeft: "4px solid var(--ajw-blue)",
          boxShadow: "0 0 20px var(--ajw-blue-30)",
          fontWeight: 600,
        }),
      }}
      data-active={isActive ? "true" : undefined}
      aria-label={sidebarLabel}
      data-sidebar-label={sidebarLabel}
      onMouseEnter={handleLinkMouseEnter}
      onMouseLeave={handleLinkMouseLeave}
      onClick={(e) => {
        handleClick(e as React.MouseEvent<HTMLAnchorElement>);
      }}
    >
      {linkContent}
      {rippleStyle && (
        <span
          className="ripple-effect"
          style={{
            position: "absolute",
            left: `${rippleStyle.x}px`,
            top: `${rippleStyle.y}px`,
            width: "20px",
            height: "20px",
            borderRadius: "50%",
            background: isDropdownItem
              ? "var(--ajw-blue-50)"
              : "var(--color-white-50)",
            transform: "translate(-50%, -50%)",
            pointerEvents: "none",
            animation: "ripple 0.6s ease-out",
            zIndex: 1,
          }}
        />
      )}
    </RouterNavLink>
  );

  return (
    <li
      key={itemKey}
      className={classes.item}
      data-compact-style={compactStyle || undefined}
      data-sidebar-label-anchor={labelEligible ? "true" : undefined}
    >
      {linkElement}
      {labelEligible ? (
        <SidebarMinimizedFlyoutPortal
          anchorRef={linkRef}
          label={item.name}
          open={minimizedFlyoutOpen}
        />
      ) : null}
    </li>
  );
};

export default NavLink;
